import { createState, State, useState } from "@hookstate/core"
import { MeetingStatus, useLocalVideo, useMeetingManager, useToggleLocalMute } from "amazon-chime-sdk-component-library-react"
import { useEffect } from "react"
import { useAppState } from "../../globalStates/AppState"
import { LogoPositions } from "../enums/LogoPosition"
import { useVideoContext } from "./VideoContext"

export enum PreMeetingSettingType {
    AUDIOINPUTSTATE = "AUDIOINPUTSTATE",
    VIDEOINPUTSTATE = "VIDEOINPUTSTATE",
    LOGOURL = "LOGOURL",
    LOGOPOSITION = "LOGOPOSITION",
    BACKGROUNDURL = "BACKGROUNDURL",
    BLUR = "BLUR"
}

export interface PreMeetingSettingsObjectType {
    audioInputState: boolean
    videoInputState: boolean
    logoUrl: string
    logoPosition: LogoPositions
    backgroundUrl: string
    blurState: boolean
}

export const preMeetingSettingsKey = "virtualGuide-PreMeetingSettings"

interface StateValues {
    meetingSettingsState: PreMeetingSettingsObjectType | undefined
}

const getStartValues = (): StateValues => {
    return {
        meetingSettingsState: undefined
    }
}

export interface PreMeetingSettingContext {
    applyStoredAudioSettings: () => Promise<void>
    applyStoredVideoSettings: () => Promise<void>
    storeSetting: (setting: PreMeetingSettingType, value: any) => void
    getSetting: (setting: PreMeetingSettingType) => any
}
const state = createState<StateValues>(getStartValues())

const useStateWrapper = (state: State<StateValues>): PreMeetingSettingContext => {
    const { muted, toggleMute } = useToggleLocalMute()
    const { toggleVideo } = useLocalVideo()
    const { setSelectedBackground, setSelectedLogo, setLogoPosition, updateVideoInput, toggleBlur, getIsBlurActive } =
        useVideoContext()
    const settingsObjectFromLocalStorage = JSON.parse(
        localStorage.getItem(preMeetingSettingsKey) || "{}"
    ) as PreMeetingSettingsObjectType
    const appState = useAppState()

    useEffect(() => {
        state.merge({ meetingSettingsState: settingsObjectFromLocalStorage })
        // eslint-disable-next-line
    }, [])
    const meetingManager = useMeetingManager()

    return {
        applyStoredAudioSettings: async () => {
            // 1. Load audio input settings and apply
            if (
                settingsObjectFromLocalStorage.hasOwnProperty("audioInputState") &&
                settingsObjectFromLocalStorage.audioInputState === false &&
                !muted
            ) {
                toggleMute()
            } else if (
                settingsObjectFromLocalStorage.hasOwnProperty("audioInputState") &&
                settingsObjectFromLocalStorage.audioInputState === true &&
                muted
            ) {
                toggleMute()
            }
        },
        applyStoredVideoSettings: async () => {
            // Load logo url and apply
            if (
                meetingManager.meetingStatus === MeetingStatus.Succeeded ||
                meetingManager.meetingStatus === MeetingStatus.Loading
            ) {
                const logoUrl: string = settingsObjectFromLocalStorage.logoUrl
                if (logoUrl) {
                    setSelectedLogo(logoUrl)
                }
                // Load logo position and apply
                const logoPosition: LogoPositions = settingsObjectFromLocalStorage.logoPosition
                if (logoPosition) {
                    setLogoPosition(logoPosition)
                }
                //  Load background url and apply
                const backgroundUrl: string = settingsObjectFromLocalStorage?.backgroundUrl
                if (backgroundUrl) {
                    setSelectedBackground(backgroundUrl)
                }

                // Load background blur state and apply
                const backgroundBlurState: boolean = settingsObjectFromLocalStorage?.blurState
                if (!getIsBlurActive() && backgroundBlurState) {
                    toggleBlur()
                } else if (getIsBlurActive() && !settingsObjectFromLocalStorage.blurState) {
                    toggleBlur()
                }

                // Load camera state
                const videoInputState: boolean = settingsObjectFromLocalStorage.videoInputState
                if (videoInputState && !appState.isAudioVideoSettingsV2Open) {
                    /**This check prevents camera toggle when settings is open and the user switches between tabs */
                    setTimeout(() => {
                        toggleVideo()
                    }, 500)
                }

                updateVideoInput()
            }
        },
        storeSetting: (setting: PreMeetingSettingType, value: any) => {
            const settingsObjectFromLocalStorage = JSON.parse(
                localStorage.getItem(preMeetingSettingsKey) || "{}"
            ) as PreMeetingSettingsObjectType
            let settingsObjectFromLocalStorageCopy = structuredClone(settingsObjectFromLocalStorage)

            switch (setting) {
                case PreMeetingSettingType.AUDIOINPUTSTATE:
                    settingsObjectFromLocalStorageCopy.audioInputState = value
                    break
                case PreMeetingSettingType.VIDEOINPUTSTATE:
                    settingsObjectFromLocalStorageCopy.videoInputState = value
                    break
                default:
            }

            localStorage.setItem(preMeetingSettingsKey, JSON.stringify(settingsObjectFromLocalStorageCopy))
            state.merge({ meetingSettingsState: settingsObjectFromLocalStorageCopy })
        },
        getSetting: (setting: PreMeetingSettingType) => {
            switch (setting) {
                case PreMeetingSettingType.AUDIOINPUTSTATE:
                    return state.value.meetingSettingsState?.audioInputState
                case PreMeetingSettingType.VIDEOINPUTSTATE:
                    return state.value.meetingSettingsState?.videoInputState
            }
        }
    }
}

export const usePreMeetingSettingContext = (): PreMeetingSettingContext => useStateWrapper(useState(state))
